import {
  BOUNDS_U,
  BOUNDS_V,
  segmentAlongXBoundary,
  segmentAlongYBoundary,
} from '@asset-viz/internalized/geometry'
import { LineDirectionX, LineDirectionY } from 'types'
import { xAlongLine, yAlongLine } from '@asset-viz/internalized/geometry'

/**
 * Splits a line segment along the x or y wrapping points, creates boundary segments if applicable,
 * and adds all resulting segments to the list uvs.
 * @param su start u
 * @param sv start v
 * @param eu end u
 * @param ev end v
 * @param uvs list to add to
 * @param xDir x direction of s -> e
 * @param yDir y direction of s -> e
 * @param wrapsX boolean for component wraps x
 * @param wrapsY boolean for component wraps y
 * @param createBoundarySegments whether extra segments should be created for segments that are on
 * boundaries (for unrolled wrapped components)
 */
export const addUVSegmentAndWrapProperly = (
  su: number,
  sv: number,
  eu: number,
  ev: number,
  uvs: number[],
  xDir: LineDirectionX,
  yDir: LineDirectionY,
  wrapsX: boolean,
  wrapsY: boolean,
  createBoundarySegments: boolean
): void => {
  if (!wrapsX && !wrapsY) {
    uvs.push(su, sv, eu, ev)
    return
  }
  uvs.push(su, sv)
  const intersection = wrapsX
    ? findXBoundaryIntersectionIfPresent(su, sv, eu, ev, xDir)
    : findYBoundaryIntersectionIfPresent(su, sv, eu, ev, yDir)
  if (intersection === undefined) {
    uvs.push(eu, ev)
    if (createBoundarySegments) {
      if (wrapsX && segmentAlongXBoundary(su, eu)) {
        if (su === BOUNDS_U[0]) {
          uvs.push(BOUNDS_U[1], sv, BOUNDS_U[1], ev)
        } else if (su === BOUNDS_U[1]) {
          uvs.push(BOUNDS_U[0], sv, BOUNDS_U[0], ev)
        }
      } else if (wrapsY && segmentAlongYBoundary(sv, ev)) {
        if (sv === BOUNDS_V[0]) {
          uvs.push(su, BOUNDS_V[1], eu, BOUNDS_V[1])
        } else if (sv === BOUNDS_V[1]) {
          uvs.push(su, BOUNDS_V[0], eu, BOUNDS_V[0])
        }
      }
    }
  } else {
    const endIntersect: [number, number] = wrapsX
      ? xDir === LineDirectionX.Right
        ? [BOUNDS_U[1], intersection]
        : [BOUNDS_U[0], intersection]
      : yDir === LineDirectionY.Up
        ? [intersection, BOUNDS_V[1]]
        : [intersection, BOUNDS_V[0]]

    const nextEndIntersect: [number, number] = wrapsX
      ? xDir === LineDirectionX.Left
        ? [BOUNDS_U[1], intersection]
        : [BOUNDS_U[0], intersection]
      : yDir === LineDirectionY.Down
        ? [intersection, BOUNDS_V[1]]
        : [intersection, BOUNDS_V[0]]
    uvs.push(...endIntersect, ...nextEndIntersect, eu, ev)
  }
}

/**
 * Finds intersection with the X wrapping boundary.
 * If there's no intersection or no endpoint, returns undefined.
 * @param startU
 * @param startV
 * @param endU
 * @param endV
 * @param xDirection
 * @returns
 */
const findXBoundaryIntersectionIfPresent = (
  startU: number,
  startV: number,
  endU: number,
  endV: number,
  xDirection: LineDirectionX
): number | undefined => {
  if (
    endU === undefined ||
    endV === undefined ||
    segmentAlongXBoundary(startU, endU)
  )
    return undefined
  if (
    xDirection !== LineDirectionX.None &&
    startU < endU !== (xDirection === LineDirectionX.Right)
  ) {
    if (xDirection === LineDirectionX.Right) {
      const newEndU = BOUNDS_U[1] + endU
      return yAlongLine([startU, startV], [newEndU, endV], BOUNDS_U[1])
    } else {
      const newEndU = endU - BOUNDS_U[1]
      return yAlongLine([startU, startV], [newEndU, endV], BOUNDS_U[0])
    }
  }
  return undefined
}

/**
 * Finds intersection with the Y wrapping boundary.
 * If there's no intersection or no endpoint, returns undefined.
 * @param startU
 * @param startV
 * @param endU
 * @param endV
 * @param yDirection
 * @returns
 */
const findYBoundaryIntersectionIfPresent = (
  startU: number,
  startV: number,
  endU: number,
  endV: number,
  yDirection: LineDirectionY
): number | undefined => {
  if (
    endU === undefined ||
    endV === undefined ||
    segmentAlongYBoundary(startV, endV)
  )
    return undefined
  if (
    yDirection !== LineDirectionY.None &&
    startV < endV !== (yDirection === LineDirectionY.Up)
  ) {
    if (yDirection === LineDirectionY.Up) {
      const newEndV = BOUNDS_V[1] + endV
      return xAlongLine([startU, startV], [endU, newEndV], BOUNDS_V[1])
    } else {
      const newEndV = endV - BOUNDS_V[1]
      return xAlongLine([startU, startV], [endU, newEndV], BOUNDS_V[0])
    }
  }
  return undefined
}
